import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useListSettings } from "../api/setting";
import dayjs from "dayjs";
import { useInvalidateAppointments, useUpdateAppointment } from "../api/app";
import PrintBilling from "./printBilling";
import { useCreateExpense, useUpdateExpense } from "../api/expenses";

const InvoiceModal = ({ show, onClose, appId, app, expense }) => {
    const [treatments, setTreatments] = useState([]);
    const [treatmentList, setTreatmentList] = useState([]);
    const [paymentMethod, setPaymentMethod] = useState("Cash");
    const [paymentStatus, setPaymentStatus] = useState("Un-paid");
    const [showPrintBilling, setShowPrintBilling] = useState(false);
    const [amountPaid, setAmountPaid] = useState(expense?.amountPaid || 0);
    const treatmentIdCounter = useRef(1);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { userInfo } = useSelector((state) => state.userLogin);
    const { data: settings } = useListSettings();
    const { mutate: createExpense } = useCreateExpense();
    const { mutate: updateExpense } = useUpdateExpense();
    const invalidateAppointments = useInvalidateAppointments();

    const appointment = app;

    useEffect(() => {
        if (settings?.data?.treatment) {
            setTreatmentList(settings.data.treatment);
        }
    }, [settings]);

    useEffect(() => {
        if (expense) {
            const treatmentsWithIds = (expense?.treatment || []).map(
                (t, index) => ({
                    ...t,
                    id: t.id || index + 1,
                })
            );
            treatmentIdCounter.current = treatmentsWithIds.length + 1;
            setTreatments(treatmentsWithIds);
            setPaymentMethod(expense?.paymentMethod || "Cash");
            setPaymentStatus(expense?.paid || "Un-paid");
        }
    }, [expense]);

    const handleTreatmentChange = (id, field, value) => {
        setTreatments((prev) =>
            prev.map((t) => (t.id === id ? { ...t, [field]: value } : t))
        );
    };

    const handleAddTreatment = () => {
        setTreatments((prev) => [
            ...prev,
            {
                id: treatmentIdCounter.current++,
                name: "",
                unit: 1,
                cost: 0,
                discount: 0,
                tax: 0,
            },
        ]);
    };

    const handleRemoveTreatment = (id) => {
        setTreatments((prev) => prev.filter((t) => t.id !== id));
    };

    const calculateTotals = () => {
        let totalCost = 0,
            totalDiscount = 0,
            totalTax = 0;
        treatments.forEach((t) => {
            totalCost += t.unit * t.cost;
            totalDiscount += t.discount || 0;
            totalTax += (t.unit * t.cost * (t.tax || 0)) / 100;
        });
        return { totalCost, totalDiscount, totalTax };
    };

    const totals = calculateTotals();

    const submitHandler = (e) => {
        e.preventDefault();
        const expenseData = {
            doctor: appointment?.doctor._id,
            patient: appointment?.patient._id,
            appointment: appId,
            invoiceNumber:
                "INV" +
                (app?.patient?.patientNumber.slice(-2) || "00") +
                (
                    Date.now().toString().slice(-2) +
                    Math.floor(Math.random() * 100)
                ).padStart(4, "0"),
            treatment: treatments.map(({ id, ...rest }) => rest),
            paymentMethod,
            paid: paymentStatus,
            amountPaid,
        };
        if (expense) {
            updateExpense({ _id: expense?._id, ...expenseData });
        } else {
            createExpense(expenseData);
        }
        onClose();
        invalidateAppointments();
    };

    return (
        <>
            {showPrintBilling && (
                <PrintBilling
                    show={showPrintBilling}
                    handleClose={() => setShowPrintBilling(false)}
                    appointment={app}
                    expense={expense}
                />
            )}
            {show && (
                <div
                    className="modal show d-block"
                    tabIndex="-1"
                    role="dialog"
                    style={{ overflow: "scroll" }}
                >
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Invoice</h5>
                                <button type="button" onClick={onClose}>
                                    <i class="fa fa-window-close"></i>
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="border-bottom pb-3">
                                    <h5>
                                        Appointment with{" "}
                                        {appointment?.doctor.name}
                                    </h5>
                                    <p>
                                        {dayjs(appointment?.date).format(
                                            "Do MMMM, hh:mm A"
                                        )}
                                    </p>
                                </div>
                                <div className="invoice-section">
                                    <h6>Invoice</h6>
                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th>Treatment</th>
                                                <th>Unit</th>
                                                <th>Cost (₹)</th>
                                                <th>Deduct (₹)</th>
                                                <th>Tax (%)</th>
                                                <th>Total (₹)</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {treatments.map((treatment) => {
                                                return (
                                                    <tr key={treatment._id}>
                                                        <td>
                                                            <select
                                                                className="form-select w-75 p-1 "
                                                                value={
                                                                    treatment.name
                                                                        ? `${treatment.name}-${treatment.cost}`
                                                                        : ""
                                                                }
                                                                onChange={(
                                                                    e
                                                                ) => {
                                                                    const value =
                                                                        e.target
                                                                            .value;
                                                                    const lastHyphenIndex =
                                                                        value.lastIndexOf(
                                                                            "-"
                                                                        );
                                                                    const name =
                                                                        value.substring(
                                                                            0,
                                                                            lastHyphenIndex
                                                                        );
                                                                    const cost =
                                                                        value.substring(
                                                                            lastHyphenIndex +
                                                                                1
                                                                        );

                                                                    handleTreatmentChange(
                                                                        treatment.id,
                                                                        "name",
                                                                        name
                                                                    );
                                                                    handleTreatmentChange(
                                                                        treatment.id,
                                                                        "cost",
                                                                        parseFloat(
                                                                            cost
                                                                        ) || 0
                                                                    );
                                                                }}
                                                                defaultValue={
                                                                    ""
                                                                }
                                                            >
                                                                <option value="">
                                                                    Select
                                                                    Treatment
                                                                </option>
                                                                {[
                                                                    // Include the current treatment if not in treatmentList
                                                                    ...(treatmentList.some(
                                                                        (t) =>
                                                                            t.name ===
                                                                                treatment?.name &&
                                                                            t.price ===
                                                                                treatment?.cost
                                                                    )
                                                                        ? []
                                                                        : [
                                                                              {
                                                                                  name: treatment?.name,
                                                                                  price: treatment?.cost,
                                                                              },
                                                                          ]),
                                                                    ...treatmentList,
                                                                ].map(
                                                                    (
                                                                        {
                                                                            name,
                                                                            price,
                                                                        },
                                                                        index
                                                                    ) => (
                                                                        <option
                                                                            key={
                                                                                index
                                                                            }
                                                                            value={`${name}-${price}`}
                                                                        >
                                                                            {`${name} - ${price}`}
                                                                        </option>
                                                                    )
                                                                )}
                                                            </select>
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={
                                                                    treatment.unit
                                                                }
                                                                onChange={(e) =>
                                                                    handleTreatmentChange(
                                                                        treatment.id,
                                                                        "unit",
                                                                        parseInt(
                                                                            e
                                                                                .target
                                                                                .value,
                                                                            10
                                                                        )
                                                                    )
                                                                }
                                                            />
                                                        </td>
                                                        <td>
                                                            {treatment.cost}
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={
                                                                    treatment.discount
                                                                }
                                                                onChange={(e) =>
                                                                    handleTreatmentChange(
                                                                        treatment.id,
                                                                        "discount",
                                                                        parseFloat(
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    )
                                                                }
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={
                                                                    treatment.tax
                                                                }
                                                                onChange={(e) =>
                                                                    handleTreatmentChange(
                                                                        treatment.id,
                                                                        "tax",
                                                                        parseFloat(
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    )
                                                                }
                                                            />
                                                        </td>
                                                        <td>
                                                            {(() => {
                                                                const total =
                                                                    (treatment.unit ||
                                                                        0) *
                                                                        (treatment.cost ||
                                                                            0) -
                                                                    (treatment.discount ||
                                                                        0) +
                                                                    (treatment.tax ||
                                                                        0);
                                                                return isNaN(
                                                                    total
                                                                ) ||
                                                                    !treatment.name
                                                                    ? 0
                                                                    : total;
                                                            })()}
                                                        </td>
                                                        <td>
                                                            <button
                                                                className="btn btn-danger btn-sm"
                                                                onClick={() =>
                                                                    handleRemoveTreatment(
                                                                        treatment.id
                                                                    )
                                                                }
                                                            >
                                                                Remove
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    <button
                                        className="btn btn-primary btn-sm"
                                        onClick={handleAddTreatment}
                                    >
                                        Add Treatment
                                    </button>
                                </div>
                                <div className="summary-section border-top pt-3">
                                    <h6>Summary</h6>
                                    <p>
                                        Total Cost: ₹
                                        {totals.totalCost.toFixed(2)}
                                    </p>
                                    <p>
                                        Total Discount: ₹
                                        {totals.totalDiscount.toFixed(2)}
                                    </p>
                                    <p>
                                        Total Tax: ₹{totals.totalTax.toFixed(2)}
                                    </p>
                                    <p>
                                        Grand Total: ₹
                                        {(
                                            totals.totalCost -
                                            totals.totalDiscount +
                                            totals.totalTax
                                        ).toFixed(2)}
                                    </p>
                                </div>
                                <div className="payment-section border-top pt-3">
                                    <h6>Payment</h6>
                                    <select
                                        className="form-select"
                                        value={paymentMethod}
                                        onChange={(e) =>
                                            setPaymentMethod(e.target.value)
                                        }
                                    >
                                        <option value="Cash">Cash</option>
                                        <option value="Card">Card</option>
                                        <option value="Cheque">Cheque</option>
                                    </select>

                                    <select
                                        className="form-select mt-2"
                                        value={paymentStatus}
                                        onChange={(e) =>
                                            setPaymentStatus(e.target.value)
                                        }
                                    >
                                        <option value="Un-paid">Un-paid</option>
                                        <option value="Paid">Paid</option>
                                    </select>
                                    <div>
                                        {paymentStatus == "Paid" && (
                                            <>
                                                <label for="amountPaid">
                                                    AmountPaid :{" "}
                                                </label>
                                                <input
                                                    type="text"
                                                    style={{ margin: "10px" }}
                                                    value={amountPaid}
                                                    onChange={(e) =>
                                                        setAmountPaid(
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </>
                                        )}
                                        {/* <div>
                                            <span>Due Amount: </span>
                                            {expense?.totalCost
                                                ? (
                                                      expense?.totalCost -
                                                          expense?.totalDiscount +
                                                          expense?.totalTax || 0
                                                  ).toFixed(2) -
                                                  expense?.amountPaid
                                                : 0}
                                        </div> */}
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                {expense && (
                                    <button
                                        className="btn btn-primary"
                                        onClick={() => {
                                            onClose();
                                            setShowPrintBilling(true);
                                        }}
                                    >
                                        Print
                                    </button>
                                )}
                                <button
                                    className="btn btn-secondary"
                                    onClick={onClose}
                                >
                                    Close
                                </button>
                                <button
                                    className="btn btn-success"
                                    onClick={submitHandler}
                                >
                                    Save Invoice
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default InvoiceModal;
